import { useEffect, useRef, useState } from "react";

/**
 * Returns a ref + `visible` flag that flips to true once the element scrolls
 * within `rootMargin` of the viewport. Used to defer mounting/hydrating heavy
 * below-the-fold sections so their React render/execution work happens on
 * scroll instead of during the initial load (reduces JS execution time / TBT).
 *
 * SSR-safe: `visible` starts false on the server and the first client render,
 * so gated content must render an equivalent placeholder until it flips.
 */
export function useInViewport<T extends HTMLElement>(rootMargin = "400px") {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el || visible) return;
    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setVisible(true);
          io.disconnect();
        }
      },
      { rootMargin },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [visible, rootMargin]);

  return { ref, visible };
}
